// rraCallSheet — the PURE half of the front-line call sheet client
// (/rra-call/:token), RRA v2.1 (W3; F2, F3, G1, G4): the wire shapes (mirroring
// apps/api/src/routes/rraCall.ts), the report form's state, validation and
// payload building, and the script / subline text. No network and no
// `@/lib/api` import (that reads Vite-only import.meta.env), so it runs under
// plain `tsx --test`. lib/rraCallClient.ts adds the authenticated calls and
// re-exports all of this.
//
// Every consumer- or caller-facing sentence comes from rraCopy.ts.

import {
  CALL_NOTE,
  CALL_OUTCOME,
  CALL_OUTCOME_SHORT,
  CALL_SHEET,
  changeShownAbove,
  disabilityQuestion,
  needQuestion,
  positionQuestion,
} from './rraCopy';

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/** One prong's answer; 'cant_say' ("They couldn't say") records no evidence. */
export type RraProngAnswer = 'yes' | 'no' | 'cant_say';

/** G1: the five outcomes, each with one effect (decided server-side). */
export type RraFrontlineOutcome =
  | 'not_reached'
  | 'call_back'
  | 'wants_writing'
  | 'spoke'
  | 'cant_help_yet';

/** G1 outcome 5: why they can't help yet. */
export type RraFrontlineCantHelpReason =
  | 'wrong_number'
  | 'wants_requester_first'
  | 'needs_own_release'
  | 'cannot_answer_or_declined'
  | 'says_already_answered'
  | 'other';

export interface RraCallView {
  state: 'open' | 'closed' | 'unavailable';
  callerFirstName: string;
  subjectName: string;
  requesterName: string;
  /** Whoever signed the permission that names this verifier. */
  signerName: string;
  verifier: { name: string; role?: string; phone?: string; email?: string };
  /** GAP-ONLY: which of the two lawful questions to ask. */
  askDisability: boolean;
  askNeed: boolean;
  questionCount: 1 | 2;
  /** rraCopy.requestSummary — "permit an assistance animal" … */
  summary: string;
  needObject: string;
  /** The requester's own words for a non-animal change, shown above the need question. */
  changeTexts: string[];
  /** The permission's signing date, long form ("October 22, 2026"). */
  signedDate: string;
  /** First sentence of this verifier's permission block (D1 grants only). */
  permissionFirstSentence?: string;
  /** A live permission names this verifier, so their copy can be shown. */
  copyAvailable: boolean;
  officePhone: string;
  nextCallSuggestedAt?: string;
  /** F3: caller, time and outcome only — no need, no note. */
  calls: Array<{ at: string; callerName: string; outcome: RraFrontlineOutcome }>;
}

export interface RraCallSubmissionResult {
  ok: boolean;
  view: RraCallView;
}

/** POST /api/rra-call/:token. The ONLY free text is `note` (<=300). */
export interface RraCallBody {
  outcome: RraFrontlineOutcome;
  /** ISO — when the call happened (server default: now). */
  at?: string;
  /** ISO — required for call_back. */
  callBackAt?: string;
  /** Required for wants_writing. */
  writing?: { email: string; readBack: boolean };
  /** Required for spoke; out-of-gap prongs are never sent (the server drops them anyway). */
  answers?: { positionToKnow: 'yes' | 'no'; disability?: RraProngAnswer; need?: RraProngAnswer };
  /** Required for cant_help_yet. */
  reason?: RraFrontlineCantHelpReason;
  /** Read only for wrong_number. */
  betterPhone?: string;
  note?: string;
}

export const CALL_NOTE_MAX = 300;

/** Outcome 3's address must look like one (the API's rule, routes/rraCall.ts). */
const PLAUSIBLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Errors (duck-typed on the api helper's ApiError: `status` + `body`)
// ---------------------------------------------------------------------------

function apiErrorParts(err: unknown): { status: number; body: unknown } | null {
  if (!(err instanceof Error)) return null;
  const status = (err as { status?: unknown }).status;
  return typeof status === 'number' ? { status, body: (err as { body?: unknown }).body } : null;
}

/** True for the 403 the caller guard sends (a signed-in login that is not a caller). */
export function isNotCallerError(err: unknown): boolean {
  return apiErrorParts(err)?.status === 403;
}

/** A sentence for a failed save: the server's own `message` (or joined per-field
 *  messages) on a 400, the roster line on a 403, else a plain retry line. */
export function callErrorMessage(err: unknown): string {
  const fallback = 'The call could not be saved. Please try again.';
  const parts = apiErrorParts(err);
  if (!parts) return fallback;
  if (parts.status === 403) return CALL_SHEET.notCaller;
  const b = parts.body as
    | { error?: string; message?: string; messages?: Record<string, string> }
    | null;
  if (b?.message) return b.message;
  if (b?.messages && Object.keys(b.messages).length > 0) return Object.values(b.messages).join(' ');
  if (b?.error && parts.status < 500) return b.error;
  return fallback;
}

// ---------------------------------------------------------------------------
// Time helpers (pure)
// ---------------------------------------------------------------------------

/** "Oct 23, 2:10 PM" in the viewer's own time zone. */
export function formatCallWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** A `datetime-local` value for `now` (local time, minutes precision). */
export function localInputNow(now: Date = new Date()): string {
  const d = new Date(now.getTime());
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

/** A `datetime-local` value → an ISO instant (undefined when empty or invalid). */
export function localInputToIso(local: string): string | undefined {
  if (!local) return undefined;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

// ---------------------------------------------------------------------------
// The report form (pure)
// ---------------------------------------------------------------------------

export interface CallFormState {
  outcome: RraFrontlineOutcome | null;
  /** datetime-local: when the call happened. */
  atLocal: string;
  /** datetime-local: outcome call_back. */
  callBackLocal: string;
  /** Outcome wants_writing: prefilled with the address on file, editable. */
  writingEmail: string;
  readBack: boolean;
  /** Outcome spoke. */
  positionToKnow: 'yes' | 'no' | undefined;
  disability: RraProngAnswer | undefined;
  need: RraProngAnswer | undefined;
  /** Outcome cant_help_yet. */
  reason: RraFrontlineCantHelpReason | undefined;
  betterPhone: string;
  /** G4 call logistics — the ONLY free text. */
  note: string;
}

export function blankCallForm(
  view: Pick<RraCallView, 'verifier'>,
  now: Date = new Date(),
): CallFormState {
  return {
    outcome: null,
    atLocal: localInputNow(now),
    callBackLocal: '',
    writingEmail: view.verifier.email ?? '',
    readBack: false,
    positionToKnow: undefined,
    disability: undefined,
    need: undefined,
    reason: undefined,
    betterPhone: '',
    note: '',
  };
}

/** G1: the five outcomes in the order the sheet lists them, with rraCopy's labels. */
export const CALL_OUTCOME_CHOICES: ReadonlyArray<{ value: RraFrontlineOutcome; label: string }> = [
  { value: 'not_reached', label: CALL_OUTCOME.not_reached },
  { value: 'call_back', label: CALL_OUTCOME.call_back },
  { value: 'wants_writing', label: CALL_OUTCOME.wants_writing },
  { value: 'spoke', label: CALL_OUTCOME.spoke },
  { value: 'cant_help_yet', label: CALL_OUTCOME.cant_help_yet },
];

/** G1 outcome 5: the six reasons, in the order the sheet lists them. */
export const CANT_HELP_REASONS: readonly RraFrontlineCantHelpReason[] = [
  'wrong_number',
  'wants_requester_first',
  'needs_own_release',
  'cannot_answer_or_declined',
  'says_already_answered',
  'other',
];

/** The questions this call may ask: exactly the recorded gap (never a default). */
export function inGapQuestions(
  view: Pick<RraCallView, 'askDisability' | 'askNeed'>,
): { disability: boolean; need: boolean } {
  return { disability: view.askDisability, need: view.askNeed };
}

/** Outcome 4 where every in-gap question was "They couldn't say" — recorded as
 *  no evidence, so the sheet steers it to outcome 5 instead. */
export function allCantSay(
  view: Pick<RraCallView, 'askDisability' | 'askNeed'>,
  form: Pick<CallFormState, 'positionToKnow' | 'disability' | 'need'>,
): boolean {
  if (form.positionToKnow !== 'yes') return false;
  const answers: Array<RraProngAnswer | undefined> = [];
  if (view.askDisability) answers.push(form.disability);
  if (view.askNeed) answers.push(form.need);
  return answers.length > 0 && answers.every((a) => a === 'cant_say');
}

/** First problem with the form as a plain sentence, or null when it can be saved.
 *  The sentences are the server's own, so a client check and a 400 read the same. */
export function callFormProblem(
  view: Pick<RraCallView, 'askDisability' | 'askNeed'>,
  form: CallFormState,
): string | null {
  switch (form.outcome) {
    case null:
      return 'Please choose what happened.'; // DRAFT
    case 'not_reached':
      return null;
    case 'call_back':
      return localInputToIso(form.callBackLocal) === undefined
        ? 'Please enter when they asked to be called back.'
        : null;
    case 'wants_writing':
      // The server's rule: the address is emailed their copy and may be stored.
      return PLAUSIBLE_EMAIL_RE.test(form.writingEmail.trim()) ? null : 'Please enter their email.'; // DRAFT
    case 'spoke': {
      if (form.positionToKnow === undefined) return 'Please answer the first question.'; // DRAFT
      if (form.positionToKnow === 'yes') {
        if (view.askDisability && form.disability === undefined) return 'Please answer each question.'; // DRAFT
        if (view.askNeed && form.need === undefined) return 'Please answer each question.'; // DRAFT
        if (allCantSay(view, form)) return CALL_OUTCOME.allCantSaySteer;
      }
      return null;
    }
    case 'cant_help_yet':
      if (form.reason === undefined) return 'Please choose why.'; // DRAFT
      if (form.reason === 'other' && form.note.trim() === '') return 'Please add a short note.'; // DRAFT
      return null;
  }
}

/** The POST body. Throws when the outcome is unset; call `callFormProblem` first. */
export function buildCallBody(
  view: Pick<RraCallView, 'askDisability' | 'askNeed'>,
  form: CallFormState,
): RraCallBody {
  if (form.outcome === null) throw new Error('buildCallBody: an outcome is required');
  const at = localInputToIso(form.atLocal);
  const note = form.note.trim();
  const body: RraCallBody = {
    outcome: form.outcome,
    ...(at ? { at } : {}),
    ...(note ? { note } : {}),
  };
  switch (form.outcome) {
    case 'call_back': {
      const callBackAt = localInputToIso(form.callBackLocal);
      if (callBackAt) body.callBackAt = callBackAt;
      break;
    }
    case 'wants_writing':
      body.writing = { email: form.writingEmail.trim(), readBack: form.readBack };
      break;
    case 'spoke':
      if (form.positionToKnow !== undefined) {
        const yes = form.positionToKnow === 'yes';
        body.answers = {
          positionToKnow: form.positionToKnow,
          ...(yes && view.askDisability && form.disability ? { disability: form.disability } : {}),
          ...(yes && view.askNeed && form.need ? { need: form.need } : {}),
        };
      }
      break;
    case 'cant_help_yet': {
      if (form.reason) body.reason = form.reason;
      const phone = form.betterPhone.trim();
      if (form.reason === 'wrong_number' && phone) body.betterPhone = phone;
      break;
    }
    case 'not_reached':
      break;
  }
  return body;
}

// ---------------------------------------------------------------------------
// Sheet text (pure) — every sentence is rraCopy's
// ---------------------------------------------------------------------------

const PHONE_MARK = '\u0001phone\u0001';

/** F3 subline split around the phone, so the page can make the number a tel: link
 *  while the sentence itself stays `CALL_SHEET.subline`. */
export function sublineSegments(
  view: Pick<RraCallView, 'verifier' | 'subjectName'>,
): { before: string; phone?: string; after: string } {
  const phone = view.verifier.phone?.trim() ?? '';
  const line = CALL_SHEET.subline(view.verifier.role?.trim() ?? '', phone ? PHONE_MARK : '', view.subjectName);
  if (!phone) return { before: line, after: '' };
  const [before = '', after = ''] = line.split(PHONE_MARK);
  return { before, phone, after };
}

/** `tel:` href for a displayed number. */
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}

export function callHeading(view: Pick<RraCallView, 'verifier'>): string {
  return CALL_SHEET.heading(view.verifier.name || 'the verifier');
}

/** F3 card "How to open the call": front desk, once-you-have, voicemail, don't record. */
export function openCallParagraphs(view: RraCallView): string[] {
  const verifierName = view.verifier.name || 'the verifier';
  const callerName = view.callerFirstName;
  return [
    CALL_SHEET.frontDesk({
      callerName,
      verifierName,
      subjectName: view.subjectName,
      questionCount: view.questionCount,
    }),
    CALL_SHEET.onceYouHave({ verifierName, subjectName: view.subjectName, signedDate: view.signedDate }),
    CALL_SHEET.voicemail({
      callerName,
      verifierName,
      subjectName: view.subjectName,
      officePhone: view.officePhone,
    }),
    CALL_SHEET.dontRecord,
  ];
}

export interface ConfirmQuestion {
  key: 'position' | 'disability' | 'need';
  text: string;
  /** The requester's own words, shown directly above this question. */
  above?: string[];
}

/** F3 card "What to confirm": the position question, then exactly the in-gap questions. */
export function confirmQuestions(view: RraCallView): ConfirmQuestion[] {
  const out: ConfirmQuestion[] = [{ key: 'position', text: positionQuestion(view.subjectName) }];
  if (view.askDisability) out.push({ key: 'disability', text: disabilityQuestion(view.subjectName) });
  if (view.askNeed) {
    out.push({
      key: 'need',
      text: needQuestion(view.subjectName, view.needObject),
      ...(view.changeTexts.length > 0 ? { above: view.changeTexts.map(changeShownAbove) } : {}),
    });
  }
  return out;
}

/** The spoken definition is read aloud only when disability is in the gap. */
export function showSpokenDefinition(view: Pick<RraCallView, 'askDisability'>): boolean {
  return view.askDisability;
}

export function confirmLead(view: Pick<RraCallView, 'subjectName' | 'summary'>): string {
  return CALL_SHEET.confirmLead(view.subjectName, view.summary);
}

export function permissionCoversLine(
  view: Pick<RraCallView, 'permissionFirstSentence' | 'signedDate'>,
): string | null {
  return view.permissionFirstSentence
    ? CALL_SHEET.permissionCovers(view.permissionFirstSentence, view.signedDate)
    : null;
}

/** F3 "Earlier calls": caller, time, outcome — no need, no note. */
export function earlierCallLine(c: RraCallView['calls'][number]): string {
  return `${c.callerName} · ${formatCallWhen(c.at)} · ${CALL_OUTCOME_SHORT[c.outcome]}`;
}

/** G4: the note label is the plain one, or the required one under "Something else". */
export function noteLabel(form: Pick<CallFormState, 'outcome' | 'reason'>): string {
  return form.outcome === 'cant_help_yet' && form.reason === 'other'
    ? CALL_NOTE.labelRequired
    : CALL_NOTE.label;
}

export function noteHelper(note: string): string {
  return CALL_NOTE.helper(note.length);
}
